import { Button, cn } from '@repo/ui';
import { Check, ChevronDown, LogOut } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useCurrentUser, useSignout } from '@/features/auth/hooks/useAuthMutations';
import { currentTenantSlug } from '@/lib/env';

/**
 * TenantSwitcher — dropdown that surfaces the current tenant slug
 * (derived from the subdomain) and exposes the sign-out action.
 *
 * In Slice 0 we only have ONE tenant per user (the one they signed
 * into via the subdomain). The full multi-membership list comes in
 * Slice 1 once `/auth/memberships` lands. Until then, this serves as
 * a "you are signed into <slug>" badge plus the sign-out entry —
 * the latter is the only function the dropdown actually performs.
 *
 * The dropdown is fully keyboard-accessible via a custom popover
 * implementation; we deliberately don't pull `@radix-ui/react-dropdown-menu`
 * yet to keep the bundle lean for the auth shell. Aria-expanded +
 * focus-move-on-Escape pattern covers the typical user paths.
 */

export interface TenantSwitcherProps {
  className?: string;
}

export function TenantSwitcher({ className }: TenantSwitcherProps): ReactElement {
  const [open, setOpen] = useState(false);
  const signout = useSignout();
  const user = useCurrentUser();
  const slug = currentTenantSlug() || 'organización';

  return (
    <div className={cn('relative', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 px-2.5 text-xs font-medium"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="truncate">{slug}</span>
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </Button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-20 cursor-default"
            aria-label="Cerrar menú"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            aria-label="Acciones de sesión"
            className="absolute right-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
          >
            <div className="border-b border-border px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Conectado como
              </p>
              <p className="truncate text-sm font-medium" title={user?.email ?? undefined}>
                {user?.email ?? '—'}
              </p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                <Check className="-mt-0.5 mr-1 inline size-3" aria-hidden="true" />
                {slug}
              </p>
            </div>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted"
              onClick={() => {
                setOpen(false);
                signout.mutate();
              }}
            >
              <LogOut className="size-3.5" aria-hidden="true" />
              Cerrar sesión
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
